import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {PaginatedResponseType} from "../Types/Response/PaginatedResponseType";
import axiosAuthorised, {DEV_OVERRIDE_HEADER} from "./AxiosAuthorised";
import {AddressType} from "../Types/AddressType";
import {AddressImportType} from "../Components/AddressImporter/AddressImportTypes";
import { PaginationPageParameters } from './PaginationHelpers';

export enum AddressDataUpdateType {
    CREATE,
    DELETE,
    UPDATE,
}

const addAddress = async (newAddress: AddressType) => {
    const {data} = await axiosAuthorised.post(
        `${process.env.REACT_APP_API_HOST}/client/v1/address`,
        JSON.stringify(newAddress),
        DEV_OVERRIDE_HEADER
    )

    return data;
}

const addBulkAddresses = async (newAddresses: Array<AddressImportType>) => {
    const {data} = await axiosAuthorised.post(
        `${process.env.REACT_APP_API_HOST}/client/v1/address/createbulk`,
        JSON.stringify(newAddresses),
        DEV_OVERRIDE_HEADER
    )

    return data;
}

const deleteAddress = async (addressId: any) => {
    const {data} = await axiosAuthorised.delete(
        `${process.env.REACT_APP_API_HOST}/client/v1/address/` + addressId,
        DEV_OVERRIDE_HEADER
    )

    return data;
}

const useInfiniteAddresses = () => {
    const baseUrl = `${process.env.REACT_APP_API_HOST}/client/v1/address/list?offset=0&limit=100`;

    return useInfiniteQuery(
        ['addressList'],
        async ({ pageParam = baseUrl }) => {
            const { data } : { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam, DEV_OVERRIDE_HEADER);
            return data;
        },
        {
            ...PaginationPageParameters,
            staleTime: 600000,
        },
    );
}

const useCountryCodes = () => {
    return useQuery(
        ['countryCodes'],
        async () => {
            const { data } : { data: PaginatedResponseType } = await axiosAuthorised.get(`${process.env.REACT_APP_API_HOST}/client/v1/country-code/List`, DEV_OVERRIDE_HEADER);
            return data;
        }
    );
}

const useUpdateAddressData = () => {
    const queryClient = useQueryClient();
    let QUERY_KEY = ['addressList'];

    return useMutation(async () => null, {
        onMutate: async ({ newData, updateType }: {newData: any, updateType: AddressDataUpdateType }) => {
            await queryClient.cancelQueries(QUERY_KEY);

            const previousData : any = queryClient.getQueryData(QUERY_KEY);

            let modifiedData: any = queryClient.getQueryData(QUERY_KEY);

            if(modifiedData) {
                switch(updateType) {
                    case AddressDataUpdateType.CREATE:
                        modifiedData.pages[modifiedData.pages.length - 1].data = [newData, ...modifiedData.pages[modifiedData.pages.length - 1].data];
                        break;
                    case AddressDataUpdateType.DELETE:
                        modifiedData.pages.map((group: any, index: number) => {
                            modifiedData.pages[index].data = group.data.filter((entry: AddressType) => {
                                return !entry.addressId !== newData;
                            })
                        });
                        break;
                    case AddressDataUpdateType.UPDATE:
                        for(let i = 0; i < modifiedData.length;i++) {
                            if(modifiedData[i].addressId === newData.addressId) {
                                modifiedData[i] = newData;
                            }
                        }
                        break;
                }

                queryClient.setQueryData(QUERY_KEY, modifiedData);
            }

            return { previousData, modifiedData }
        },
        onError: (err, newData: any, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previousData)
        },
        onSettled: () => {
            queryClient.invalidateQueries(QUERY_KEY).then(() => null);
        },
    })
}

const useBulkUpdateAddressData = () => {
    const queryClient = useQueryClient();
    let QUERY_KEY = ['addressList'];

    return useMutation(async () => null, {
        onMutate: async ({ newData, updateType }: {newData: any, updateType: AddressDataUpdateType }) => {
            await queryClient.cancelQueries(QUERY_KEY);

            const previousData : any = queryClient.getQueryData(QUERY_KEY);

            let modifiedData: any = queryClient.getQueryData(QUERY_KEY);

            if(modifiedData) {
                modifiedData.pages[modifiedData.pages.length - 1].data = [...newData, ...modifiedData.pages[modifiedData.pages.length - 1].data];
                queryClient.setQueryData(QUERY_KEY, modifiedData);
            }

            return { previousData, modifiedData }
        },
        onError: (err, newData: any, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previousData)
        },
        onSettled: () => {
            queryClient.invalidateQueries(QUERY_KEY).then(() => null);
        },
    })
}

export { addAddress, addBulkAddresses, deleteAddress, useInfiniteAddresses, useCountryCodes, useUpdateAddressData, useBulkUpdateAddressData }
